import { Product } from '../types/Product';
import { MenuItem, MenuSuggestion } from '../types/Menu';

const calculateNutritionalBalance = (products: Product[]): boolean => {
  const categories = products.map(p => p.category_name);
  const hasVegetables = categories.some(c => c.includes('Verdura') || c.includes('Fruta'));
  const hasProtein = categories.some(c => c.includes('Carne') || c.includes('Pescado'));
  const hasCarbs = categories.some(c => c.includes('Pasta') || c.includes('Arroz'));
  
  return hasVegetables && hasProtein && hasCarbs;
};

const generateMenuItem = (
  products: Product[],
  maxPrice: number,
  preferredCategories: string[]
): MenuItem | null => {
  // Filter products by category and price
  const filteredProducts = products.filter(p => 
    preferredCategories.some(cat => p.category_name.toLowerCase().includes(cat.toLowerCase())) &&
    p.current_price <= maxPrice * 0.8 // Leave room for combinations
  );

  if (filteredProducts.length === 0) return null;

  // Try to create combinations that fit within the budget
  let selectedProducts: Product[] = [];
  let currentTotal = 0;
  const shuffledProducts = [...filteredProducts].sort(() => Math.random() - 0.5);

  for (const product of shuffledProducts) {
    if (currentTotal + product.current_price <= maxPrice) {
      selectedProducts.push(product);
      currentTotal += product.current_price;
    }

    // Stop if we have enough products or reached price limit
    if (selectedProducts.length >= 3 || currentTotal >= maxPrice * 0.9) break;
  }

  if (selectedProducts.length === 0) return null;

  return {
    name: `${selectedProducts[0].category_name} especial`,
    products: selectedProducts.map(p => p.id),
    totalPrice: currentTotal,
    category: selectedProducts[0].category_name,
    isBalanced: calculateNutritionalBalance(selectedProducts)
  };
};

export const generateMenuSuggestion = (
  products: Product[],
  maxPricePerDish: number,
  courseCount: number = 1,
  type: string = 'balanced'
): MenuSuggestion => {
  const menuItems: MenuItem[] = [];

  const categories = {
    starters: ['verdura', 'ensalada', 'sopa'],
    mains: ['carne', 'pescado', 'pasta'],
    desserts: ['fruta', 'postre']
  };

  if (type === 'economic') {
    categories.mains.push('arroz', 'legumbre');
  } else if (type === 'premium') {
    categories.mains = ['carne', 'pescado'];
  }

  // Adjust price distribution based on course count
  const priceDistribution = {
    1: [1],
    2: [0.4, 0.6],
    3: [0.3, 0.5, 0.2]
  };

  const coursePrices = priceDistribution[courseCount as keyof typeof priceDistribution] || [1];

  if (courseCount === 1) {
    const mainDish = generateMenuItem(
      products, 
      maxPricePerDish, 
      [...categories.mains, ...categories.starters]
    );
    if (mainDish) menuItems.push(mainDish);
  } else {
    // Generate courses with distributed budget
    const courseTypes = [
      { categories: categories.starters, index: 0 },
      { categories: categories.mains, index: 1 },
      { categories: categories.desserts, index: 2 }
    ].slice(0, courseCount);

    for (const { categories: cats, index } of courseTypes) {
      const courseBudget = maxPricePerDish * coursePrices[index];
      const course = generateMenuItem(products, courseBudget, cats);
      if (course) menuItems.push(course);
    }
  }

  const totalPrice = menuItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const courseName = courseCount === 1 ? 'plato' : 'platos';

  return {
    name: `Menú ${type.charAt(0).toUpperCase() + type.slice(1)} - ${courseCount} ${courseName}`,
    items: menuItems,
    totalPrice,
    description: `Un menú ${type} con ${menuItems.length} ${courseName} equilibrados`
  };
};